import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Heading,
  Input,
  LoadingSpinner,
  Select,
  Text,
  Tooltip,
} from '../../components/ui'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import {
  deleteFlowchart,
  getFlowchartVersions,
  listFlowcharts,
  restoreFlowchartVersion,
} from '../../services/qualityFlowcharts'
import type {
  QualityFlowchart,
  QualityFlowchartStatus,
  QualityFlowchartVersion,
} from '../../types/qualityFlowchart'
import {
  ClockIcon,
  PencilIcon,
  PlayIcon,
  PlusIcon,
  SparklesIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { MODULE_NAME } from './Fluxograma'

const LOG_ORIGEM = 'qualidade/fluxograma'
const ENTITY_TYPE = 'process_flowchart' as const
const ITEMS_PER_PAGE = 25

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function graphStats(graph: QualityFlowchartVersion['flow_json']): string {
  const nodes = Array.isArray(graph?.nodes) ? graph.nodes.length : 0
  const edges = Array.isArray(graph?.edges) ? graph.edges.length : 0
  return `${nodes} nós · ${edges} conexões`
}

const FluxogramaLista: React.FC = () => {
  const navigate = useNavigate()
  const { hasPermission: canInsert, loading: loadingInsert } = usePermission(
    MODULE_NAME,
    'insert'
  )
  const { hasPermission: canUpdate, loading: loadingUpdate } = usePermission(
    MODULE_NAME,
    'update'
  )
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')
  const { logUpdate, logDelete } = useUserLogs()

  const permissionsReady = !loadingInsert && !loadingUpdate
  const canSeeDrafts = canInsert || canUpdate

  const [items, setItems] = useState<QualityFlowchart[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  /** '' = todos os status */
  const [statusFilter, setStatusFilter] = useState<'' | QualityFlowchartStatus>(
    ''
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [totalItems, setTotalItems] = useState(0)
  const [creating, setCreating] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const [showVersionsModal, setShowVersionsModal] = useState(false)
  const [versionsItem, setVersionsItem] = useState<QualityFlowchart | null>(null)
  const [versions, setVersions] = useState<QualityFlowchartVersion[]>([])
  const [versionsLoading, setVersionsLoading] = useState(false)
  const [showVersionViewModal, setShowVersionViewModal] = useState(false)
  const [viewingVersion, setViewingVersion] =
    useState<QualityFlowchartVersion | null>(null)
  const [restoring, setRestoring] = useState(false)

  /** Evita resposta antiga (permissões ainda carregando) sobrescrever a lista correta */
  const loadSeqRef = useRef(0)

  const loadData = useCallback(
    async (
      page = currentPage,
      query = appliedSearch,
      status: '' | QualityFlowchartStatus = statusFilter
    ) => {
      if (!permissionsReady) return

      const seq = ++loadSeqRef.current
      setLoading(true)
      setError(null)
      try {
        // Sem permissão de edição: rascunhos não são listados (só após permissionsReady)
        const resolvedStatus: QualityFlowchartStatus | undefined = !canSeeDrafts
          ? 'published'
          : status || undefined
        const result = await listFlowcharts(
          {
            query: query || undefined,
            status: resolvedStatus,
            sort_by: 'updated_at',
            sort_order: 'desc',
          },
          page,
          ITEMS_PER_PAGE
        )
        if (seq !== loadSeqRef.current) return
        setItems(result.items)
        setTotalPages(result.totalPages)
        setTotalItems(result.total)
        setCurrentPage(result.page)
      } catch (err) {
        if (seq !== loadSeqRef.current) return
        setError(err instanceof Error ? err.message : 'Erro ao carregar fluxogramas')
      } finally {
        if (seq === loadSeqRef.current) setLoading(false)
      }
    },
    [
      appliedSearch,
      currentPage,
      statusFilter,
      canSeeDrafts,
      permissionsReady,
    ]
  )

  useEffect(() => {
    if (!permissionsReady) {
      setLoading(true)
      return
    }
    void loadData(currentPage, appliedSearch, statusFilter)
  }, [loadData, currentPage, appliedSearch, statusFilter, permissionsReady])

  // Busca ao digitar (sem botão de pesquisa)
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = searchTerm.trim()
      setAppliedSearch((prev) => {
        if (prev === next) return prev
        setCurrentPage(1)
        return next
      })
    }, 300)
    return () => window.clearTimeout(timer)
  }, [searchTerm])

  const handleStatusFilterChange = (
    value: '' | QualityFlowchartStatus
  ) => {
    setCurrentPage(1)
    setStatusFilter(value)
  }

  const handleCreate = async () => {
    if (!canInsert || creating) return
    setCreating(true)
    setError(null)
    try {
      // Abre editor vazio; o primeiro Salvar (ou salvar ao sair) cria o registro
      navigate('/qualidade/fluxograma/novo')
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (item: QualityFlowchart) => {
    if (!canDelete || deletingId) return
    if (
      !window.confirm(
        `Excluir o fluxograma "${item.title}"? Ele sairá da listagem (exclusão lógica).`
      )
    ) {
      return
    }
    setDeletingId(item.id)
    setError(null)
    try {
      await deleteFlowchart(item.id)
      await logDelete(ENTITY_TYPE, item.id, `Excluiu fluxograma "${item.title}"`, {
        origem: LOG_ORIGEM,
        id: item.id,
        title: item.title,
        description: item.description,
      })
      await loadData(currentPage, appliedSearch)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir fluxograma')
    } finally {
      setDeletingId(null)
    }
  }

  const openVersionsModal = async (item: QualityFlowchart) => {
    setVersionsItem(item)
    setShowVersionsModal(true)
    setVersionsLoading(true)
    try {
      const data = await getFlowchartVersions(item.id)
      setVersions(data)
    } catch (err) {
      console.error('Erro ao carregar versões:', err)
      setVersions([])
      setError(err instanceof Error ? err.message : 'Erro ao carregar versões')
    } finally {
      setVersionsLoading(false)
    }
  }

  const handleRestoreVersion = async (versionNumber: number) => {
    if (!canUpdate || !versionsItem || restoring) return
    if (versionsItem.status !== 'published') {
      setError('Somente fluxogramas publicados possuem histórico de versões')
      return
    }
    if (
      !window.confirm(
        `Restaurar a versão ${versionNumber}? Uma nova versão será criada com o conteúdo restaurado.`
      )
    ) {
      return
    }
    setRestoring(true)
    try {
      await restoreFlowchartVersion(versionsItem.id, versionNumber)
      await logUpdate(
        ENTITY_TYPE,
        versionsItem.id,
        `Restaurou versão ${versionNumber} do fluxograma: ${versionsItem.title}`,
        { current_version: versionsItem.current_version },
        { restored_from_version: versionNumber },
        { origem: LOG_ORIGEM }
      )
      setShowVersionsModal(false)
      setShowVersionViewModal(false)
      setVersionsItem(null)
      setVersions([])
      setViewingVersion(null)
      await loadData(currentPage, appliedSearch)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao restaurar versão')
    } finally {
      setRestoring(false)
    }
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-6">
        <div className="flex flex-1 flex-col sm:flex-row gap-2 sm:items-center max-w-3xl">
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por título ou descrição…"
            className="flex-1 min-w-0"
          />
          <div className="w-full sm:w-48 shrink-0">
            <Select
              value={
                !permissionsReady || canSeeDrafts ? statusFilter : 'published'
              }
              onChange={(e) =>
                handleStatusFilterChange(
                  e.target.value as '' | QualityFlowchartStatus
                )
              }
              disabled={!permissionsReady || !canSeeDrafts}
              aria-label="Filtrar por status"
              title="Status"
            >
              {(!permissionsReady || canSeeDrafts) && (
                <option value="">Todos</option>
              )}
              <option value="published">Publicado</option>
              {(!permissionsReady || canSeeDrafts) && (
                <option value="draft">Rascunho</option>
              )}
            </Select>
          </div>
        </div>
        {canInsert && (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/qualidade/fluxograma/novo?ia=1')}
              className="inline-flex items-center gap-2"
            >
              <SparklesIcon className="h-5 w-5" />
              Criar com IA
            </Button>
            <Button
              type="button"
              onClick={() => void handleCreate()}
              disabled={creating}
              className="inline-flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              {creating ? 'Criando…' : 'Novo fluxograma'}
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 px-6 py-16 text-center">
          <Text size="lg" className="text-gray-700 dark:text-gray-200 mb-2">
            Nenhum fluxograma encontrado
          </Text>
          <Text size="sm" color="muted">
            {canInsert
              ? 'Crie o primeiro fluxograma para começar.'
              : 'Não há fluxogramas disponíveis no momento.'}
          </Text>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Título
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Autor
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Versão
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Atualizado em
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                    onClick={() => {
                      if (item.status === 'published') {
                        navigate(
                          `/qualidade/fluxograma/${item.id}?modo=apresentacao`
                        )
                        return
                      }
                      if (canUpdate) {
                        navigate(
                          `/qualidade/fluxograma/${item.id}?modo=edicao`
                        )
                      }
                    }}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {item.title}
                      </div>
                      {item.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate max-w-md">
                          {item.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          item.status === 'published'
                            ? 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                            : 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                        }
                      >
                        {item.status === 'published' ? 'Publicado' : 'Rascunho'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {item.created_by_name || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 tabular-nums">
                      {item.status === 'published'
                        ? `v${item.current_version || 0}`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {formatDateTime(item.updated_at)}
                    </td>
                    <td
                      className="px-4 py-3 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="inline-flex items-center gap-1">
                        {item.status === 'published' && (
                          <Tooltip content="Apresentar">
                            <button
                              type="button"
                              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                              aria-label={`Apresentar ${item.title}`}
                              onClick={() =>
                                navigate(
                                  `/qualidade/fluxograma/${item.id}?modo=apresentacao`
                                )
                              }
                            >
                              <PlayIcon className="h-5 w-5" />
                            </button>
                          </Tooltip>
                        )}
                        {item.status === 'published' && (
                          <Tooltip content="Histórico de versões">
                            <button
                              type="button"
                              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                              aria-label={`Versões de ${item.title}`}
                              onClick={() => void openVersionsModal(item)}
                            >
                              <ClockIcon className="h-5 w-5" />
                            </button>
                          </Tooltip>
                        )}
                        {canUpdate && (
                          <Tooltip content="Editar">
                            <button
                              type="button"
                              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                              aria-label={`Editar ${item.title}`}
                              onClick={() =>
                                navigate(
                                  `/qualidade/fluxograma/${item.id}?modo=edicao`
                                )
                              }
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip content="Excluir">
                            <button
                              type="button"
                              className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                              disabled={deletingId === item.id}
                              aria-label={`Excluir ${item.title}`}
                              onClick={() => void handleDelete(item)}
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <Text size="sm" color="muted">
                {totalItems} fluxograma{totalItems === 1 ? '' : 's'}
              </Text>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <Text size="sm" className="px-2 self-center">
                  {currentPage} / {totalPages}
                </Text>
                <Button
                  type="button"
                  variant="outline"
                  disabled={currentPage >= totalPages}
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {showVersionsModal && versionsItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                  Histórico: {versionsItem.title}
                </Heading>
                <Text size="sm" color="muted" className="mt-1">
                  Versão atual: v{versionsItem.current_version || 0}
                </Text>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowVersionsModal(false)
                  setVersionsItem(null)
                  setVersions([])
                }}
              >
                <XMarkIcon className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {versionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : versions.length === 0 ? (
                <Text color="muted">Nenhuma versão encontrada.</Text>
              ) : (
                <div className="space-y-3">
                  {versions.map((version) => (
                    <div
                      key={version.id}
                      className="rounded-lg border border-gray-200 dark:border-gray-700 p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <Text className="font-semibold">
                            v{version.version_number}
                          </Text>
                          <Text size="sm" color="muted">
                            {formatDateTime(version.created_at)} ·{' '}
                            {version.user_name || 'Usuário'}
                          </Text>
                          <Text
                            size="sm"
                            className="mt-1 text-gray-600 dark:text-gray-300"
                          >
                            {version.change_summary ||
                              'Sem descrição das mudanças'}
                          </Text>
                          <Text size="sm" color="muted" className="mt-1">
                            {graphStats(version.flow_json)}
                          </Text>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setViewingVersion(version)
                              setShowVersionViewModal(true)
                            }}
                          >
                            Detalhes
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setShowVersionsModal(false)
                              navigate(
                                `/qualidade/fluxograma/${versionsItem.id}?modo=apresentacao&versao=${version.version_number}`
                              )
                            }}
                          >
                            Apresentar
                          </Button>
                          {canUpdate && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 hover:text-green-700"
                              disabled={restoring}
                              onClick={() =>
                                void handleRestoreVersion(version.version_number)
                              }
                            >
                              Restaurar
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showVersionViewModal && viewingVersion && versionsItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                  Versão {viewingVersion.version_number}
                </Heading>
                <Text size="sm" color="muted" className="mt-1">
                  {viewingVersion.title}
                </Text>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowVersionViewModal(false)}
              >
                <XMarkIcon className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <Text size="sm" className="font-semibold mb-1">
                  Data
                </Text>
                <Text size="sm" color="muted">
                  {formatDateTime(viewingVersion.created_at)} ·{' '}
                  {viewingVersion.user_name || 'Usuário'}
                </Text>
              </div>
              <div>
                <Text size="sm" className="font-semibold mb-1">
                  Resumo
                </Text>
                <Text size="sm" className="text-gray-700 dark:text-gray-300">
                  {viewingVersion.change_summary || '—'}
                </Text>
              </div>
              {viewingVersion.description && (
                <div>
                  <Text size="sm" className="font-semibold mb-1">
                    Descrição
                  </Text>
                  <Text size="sm" className="whitespace-pre-wrap">
                    {viewingVersion.description}
                  </Text>
                </div>
              )}
              <div>
                <Text size="sm" className="font-semibold mb-1">
                  Conteúdo
                </Text>
                <Text size="sm" color="muted">
                  {graphStats(viewingVersion.flow_json)}
                </Text>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowVersionViewModal(false)
                    setShowVersionsModal(false)
                    navigate(
                      `/qualidade/fluxograma/${versionsItem.id}?modo=apresentacao&versao=${viewingVersion.version_number}`
                    )
                  }}
                >
                  Apresentar esta versão
                </Button>
                {canUpdate && (
                  <Button
                    size="sm"
                    variant="solid"
                    disabled={restoring}
                    onClick={() =>
                      void handleRestoreVersion(viewingVersion.version_number)
                    }
                  >
                    Restaurar
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FluxogramaLista
